// © Microsoft Corporation. All rights reserved.

import { appEnvironment, Environment, SubscriptionProductTypes } from "@msnews/core";
import { Telemetry } from "@msnews/telemetry-contracts";

import {
    CreateSubscriptionParams, ManageSubscriptionParams, LinkToPublisherParams,
    GetSubscriptionsForPublisherParams, GetSubscriptionsForPublisherResponse,
    RenewSubscriptionParams, GetSubscriptionsParams, GetSubscriptionsResponse,
    UnlinkPublisherParams, SubscriptionSDKInitParams
} from "./Interfaces";

const prodSdkUrl = "https://xpaycdn.azureedge.net/js/pack/xpaySubscription.js";
const intSdkUrl = "https://xpaycdn-int.azureedge.net/js/pack/xpaySubscription.js";

let subscriptionContainerId: string;

// SDK is already initialized if `subscriptionContainerId` has a value
const isAlreadyInitialized = (): boolean => !!subscriptionContainerId;

// `sdkLoaded` promise is resolved when the SDK source file is loaded
let sdkLoadedResolver: (value: boolean) => void;
export const sdkLoaded: Promise<boolean> = new Promise(resolve => {
    sdkLoadedResolver = resolve;
});

function getSdkUrl(): string {
    switch (appEnvironment.Environment) {
        case Environment.int:
        case Environment.int1:
        case Environment.intpr:
        case Environment.intpef:
            return intSdkUrl;
        case Environment.prod:
        default:
            return prodSdkUrl;
    }
}

export function initializeSubscriptionSdk() {
    if (!isAlreadyInitialized()) {
        const sdkScript = document.createElement("script");
        sdkScript.onload = () => {
            sdkLoadedResolver(true);
            init({
                env: getSdkEnv(),
                requestId: Telemetry ? Telemetry.getRequestId() : appEnvironment.ActivityIdLowerCaseNoHypens
            });
        };
        sdkScript.src = getSdkUrl();
        sdkScript.defer = true;
        document.body.appendChild(sdkScript);

        subscriptionContainerId = "subscription-sdk-container";
        const subscriptionContainer = document.createElement("div");
        subscriptionContainer.id = subscriptionContainerId;
        document.body.appendChild(subscriptionContainer);
    }
}

export async function init(params: SubscriptionSDKInitParams) {
    return callSdkFunction("init", params);
}

export async function renderCreateSubscription(params: CreateSubscriptionParams) {
    return callSdkFunction("renderCreateSubscription", params);
}

export async function renderManageSubscription(params: ManageSubscriptionParams) {
    return callSdkFunction("renderManageSubscription", params);
}

export async function renewSubscription(params: RenewSubscriptionParams) {
    return callSdkFunction("renewSubscription", params);
}

export async function renderLinkToPublisher(params: LinkToPublisherParams) {
    return callSdkFunction("renderLinkToPublisher", params);
}

export async function getSubscriptionsForPublisher(params: GetSubscriptionsForPublisherParams): Promise<GetSubscriptionsForPublisherResponse> {
    return callSdkFunction("getSubscriptionsForPublisher", params);
}

export async function getSubscriptions(params: GetSubscriptionsParams): Promise<GetSubscriptionsResponse> {
    return callSdkFunction("getSubscriptions", params);
}

export async function unlinkPublisher(params: UnlinkPublisherParams): Promise<Boolean> {
    return callSdkFunction("unlinkPublisher", params);
}

export enum ProductTypes {
    Basic = 0,
    Advanced = 1,
    Premium = 2
}

export function mapSdkProductTypeToMsnProductType(sdkProductType: ProductTypes) {
    return sdkProductType + 1;
}

export function mapMsnProductTypeToSdkProductType(msnProductType: SubscriptionProductTypes) {
    return msnProductType - 1;
}

export enum EnvList {
    PROD = 0,
    CI = 1,
    TEST = 2,
    INT = 3
}

async function callSdkFunction(functionName: string, params) {
    await sdkLoaded;
    const func = window.xpaySubscription[functionName];
    params = await ensureRequiredParams(params);
    return func(params);
}

async function ensureRequiredParams(params) {
    return {
        ...params,
        env: params.env !== undefined ? params.env : getSdkEnv(),
        tileContainerId: subscriptionContainerId
    };
}

export function getSdkEnv(): EnvList {
    switch (appEnvironment.Environment) {
        case Environment.int:
        case Environment.int1:
        case Environment.intpr:
        case Environment.intpef:
            return EnvList.INT;
        case Environment.prod:
        default:
            return EnvList.PROD;
    }
}

export enum SubscriptionStatus {
    Active = "Active",
    Expired = "Expired",
    Canceled = "Canceled",
    PaymentFailed = "PaymentFailed"
}
